import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Subject } from 'rxjs';

import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';

import { BaseForm } from '../../../../base-form';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { FormRequestTypes } from '../../../../../common/classes/form-request';
import { environment } from '../../../../../../environments/environment';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';

import { FincasEnvironmentDefinition } from '../environment-variables';
import { FincasFormDefinition } from './form-definition';
import { ReactiveFormBuilderComponent } from '../../../../../common/components/reactive-form-builder/form-builder.component';

import { FincasModel } from '../../../../../models/form-common/fincas.module';
import { ClienteModel } from '../../../../../models/cliente.model';
import { BaseResponseModel } from 'src/app/models/base-response.model';

const FINCA_NAME = environment.features.fincaName as string;

/** Literal tokens used in list builds */
const LIST_TOKENS = {
  PRODUCT_TYPE: 'fertilizante',
  LABEL_KEY: 'nombre',
} as const;

/** Minimal typing for server responses */
interface ReaImportData {
  parcelasNoInsertadas?: number;
  parcelasInsertadas?: number;
  parcelasNoActualizadas?: number;
  parcelasActualizadas?: number;
  errores?: Array<{ descripcion: string }>;
}

@Component({
  selector: 'app-finca-form',
  standalone: false,
  templateUrl: './finca-form.component.html',
  styleUrls: ['./finca-form.component.scss'],
})
export class FincaFormComponent extends BaseForm<FincasModel> implements OnInit {
  @ViewChild(ReactiveFormBuilderComponent)
  public formBuilder!: ReactiveFormBuilderComponent<FincasModel>;

  /** Reactive form definition bound to the template */
  public form!: FincasFormDefinition;

  /** Form request engine (phase-based hooks) */
  public readonly formRequest!: ReactiveFormBuilderComponent<FincasModel>;

  /** Environment flags for feature toggles and labels */
  public readonly env: FincasEnvironmentDefinition = new FincasEnvironmentDefinition();

  /** Emits when dropdowns are ready to be reloaded */
  public readonly dropdownLoadTrigger = new Subject<void>();

  /** Expose enums/tokens to the template */
  public readonly HTTP = HttpStatusCode;

  public readonly FormRequestTypes = FormRequestTypes;

    
  public historicoREA: null = null;

  public error = '';

  constructor(
    public override router: Router,
    public override route: ActivatedRoute,
    public override dashboard: DashboardService,
    public fincasApi: FincasApiService,
    public clientesApi: ClientesApiService,
    public productosApi: ProductosApiService,
    private readonly http: HttpClient
  ) {
    super(
      route,
      router,
      dashboard,
      fincasApi.fincasPUT,
      fincasApi.fincasPOST,
      'finca',
      `Editar ${FINCA_NAME}`,
      `Crear ${FINCA_NAME}`,
      `Duplicar ${FINCA_NAME}`
    );
    this.formRequest = new ReactiveFormBuilderComponent(this.http);
  }

  /** Disabled state for REA button (no string/length literals in template) */
  public get isReaDisabled(): boolean {
    const code = this.form?.model?.codigo_REA;

    return (code == null) || String(code).trim().length === 0;
  }

  public onReaImported(result: BaseResponseModel<ReaImportData>): void {
    if (result.statusCode == 202) {
      this.fincasApi.fincasGET.safePerform();
    }
  }

  ngOnInit(): void {
    const type = this.getType();
    const baseModel = this.getModel() as unknown as Record<string, string>;

    this.form = new FincasFormDefinition(type, baseModel, null);

    void this.initFormData();

    if (this.env.fincasNifAutocomplete && type === FormRequestTypes.CREATE) {      
      this.form.model.nif = StorageManager.getClient().nif ?? {};
      this.form.model.telefono = StorageManager.getClient().telefono ?? '';
    }

    if (!this.env.activateREA) {
      const loggedInClient = StorageManager.getUser();
      
      this.form.model.id_cliente = loggedInClient.id;
    }

    this.softInit(type);
  }

  public formChanges(formModel: object, _tag: string): void {
    this.form.model = formModel as FincasModel;
  }

  /** Build dropdown data for the form and notify reactive-form-builder. */
  /**
   * Build dropdown data for the form and notify the reactive form-builder.
   * Triggers `dropdownLoadTrigger` even when there are no build tasks.
   */
  private initFormData(): Promise<boolean> {
    type BuildTask = (resolve: (ok: boolean) => void) => void;

    const builds: BuildTask[] = [];

    // Collect tasks conditionally
    if (this.env.hasClients) {
      builds.push(
        build(this.clientesApi.clientesGET, this.form.lists.clientes,
        (it: ClienteModel) => `${it.nombre  } ${  it.apellidos || ''  } ${it.apellido_1 || '' } ${it.apellido_2 || '' }`,
        false),
      );
    }

    if (this.env.showFincasProductos) {
      builds.push(
        buildParams(
          this.productosApi.productos.GET,
          { tipo: LIST_TOKENS.PRODUCT_TYPE },
          this.form.lists.productos,
          LIST_TOKENS.LABEL_KEY,
          false,
          true
        )
      );
    }

    // Common notifier: run in a microtask to mirror async pool completion timing
    const notify = (): void => { queueMicrotask(() => { this.dropdownLoadTrigger.next(); }); };

    // No tasks -> fire trigger efficiently and resolve
    if (builds.length === 0) {
      notify();
      return Promise.resolve(true);
    }

    // Tasks exist -> fire trigger after pool resolves
    return pool(builds, notify);
  }

}
